import {
  dedupeRulesForPackageProfile,
  dynamicPackageAttractionLineBillableToCustomer,
  partitionOptionalUpsellRules,
  partitionRulesForPackageProfile,
} from "../packageAttractionRulesDisplay";
import { normalizeAttraction } from "./normalizeAttraction";
import { optionalAttractionLineSubtotalIls } from "./attractionPricingContract.js";
import { resolveAdultChildUnitPricesIls } from "./resolveAdultChildUnitPricesIls";
import { resolveOptionalUnitPriceIls } from "./resolveOptionalUnitPriceIls";

/**
 * @typedef {import("./normalizeAttraction.js").NormalizedAttraction} NormalizedAttraction
 */

/** @param {Record<string, unknown>} rule */
function deriveGiftStatusLabel(rule) {
  const b = String(rule?.badge_text || "").toLowerCase();
  if (b.includes("מתנה") || b.includes("gift")) return "מתנה";
  return "הטבה";
}

function ruleToAttractionRaw(rule) {
  const cat =
    rule?.catalog && typeof rule.catalog === "object" ? rule.catalog : {};
  const aid = rule?.attraction_id;
  const name =
    (rule?.display_text != null && String(rule.display_text).trim()) ||
    (rule?.name != null && String(rule.name).trim()) ||
    (cat?.name != null && String(cat.name).trim()) ||
    (rule?.badge_text != null && String(rule.badge_text).trim()) ||
    "";
  const descShort =
    cat.description_short != null
      ? String(cat.description_short).trim()
      : rule?.description_short != null
        ? String(rule.description_short).trim()
        : "";
  return {
    ...cat,
    ...rule,
    attraction_id: aid,
    id: aid ?? rule?.id,
    name: name || String(cat?.name || "").trim(),
    display_text: rule?.display_text ?? name,
    description_short: descShort,
    description:
      cat.description != null
        ? String(cat.description)
        : rule?.description != null
          ? String(rule.description)
          : descShort,
    image_url: cat.image_url ?? rule?.image_url,
  };
}

/**
 * @param {unknown} raw
 * @returns {Record<string, { adults: number, children: number }>}
 */
function normalizeSelectedQuantities(raw) {
  /** @type {Record<string, { adults: number, children: number }>} */
  const out = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [k, v] of Object.entries(raw)) {
    const id = String(k);
    const adults = Math.max(0, Math.floor(Number(v?.adults) || 0));
    const children = Math.max(0, Math.floor(Number(v?.children) || 0));
    out[id] = { adults, children };
  }
  return out;
}

/**
 * @param {{
 *   rules: unknown[],
 *   lines?: unknown[],
 *   serverBillableTotalGross?: number,
 *   selectedOptionalQuantities?: Record<string, { adults?: number, children?: number }>,
 *   currency?: string | null,
 * }} input
 * @remarks מחירי יחידה: resolveAdultChildUnitPricesIls (כולל fallback ל־price_value/sell_value). אטרקציה עם display_mode=upsell בחוק — בבלוק אופציונלי; "כלול במחיר" דורש חוק עם display/pricing מתאימים או שורה ב־package_attractions.
 */
export function buildPackageAttractionsViewModel(input) {
  const rules = Array.isArray(input?.rules) ? input.rules : [];
  const lines = Array.isArray(input?.lines) ? input.lines : [];
  const serverBillableTotalGross = Number(input?.serverBillableTotalGross) || 0;
  const selectedQty = normalizeSelectedQuantities(input?.selectedOptionalQuantities);
  const currency = input?.currency != null ? String(input.currency) : "ILS";

  const deduped = dedupeRulesForPackageProfile(rules);
  const { included, gifts } = partitionRulesForPackageProfile(deduped);
  const optionalRules = partitionOptionalUpsellRules(deduped);

  const lineByAttractionId = new Map();
  for (const row of lines) {
    const aid = row?.attraction_id;
    if (aid == null || Number.isNaN(Number(aid))) continue;
    lineByAttractionId.set(Number(aid), row);
  }

  const optionalAidSet = new Set(
    optionalRules
      .map((r) => (r?.attraction_id != null ? Number(r.attraction_id) : NaN))
      .filter((n) => Number.isFinite(n))
  );

  /** @type {NormalizedAttraction[]} */
  const includedAttractions = included.map((r) => {
    const n = normalizeAttraction(ruleToAttractionRaw(r), "order");
    return { ...n, packageRowStatusLabel: "כלול במחיר" };
  });
  /** @type {NormalizedAttraction[]} */
  const giftAttractions = gifts.map((r) => {
    const n = normalizeAttraction(ruleToAttractionRaw(r), "order");
    return { ...n, packageRowStatusLabel: deriveGiftStatusLabel(r) };
  });

  /** @type {NormalizedAttraction[]} */
  const optionalAttractions = [];
  /** @type {Array<Record<string, unknown>>} */
  const optionalRows = [];

  for (const r of optionalRules) {
    const aid = r?.attraction_id != null ? Number(r.attraction_id) : NaN;
    const line = Number.isFinite(aid) ? lineByAttractionId.get(aid) : null;
    const raw = ruleToAttractionRaw(r);
    const n = normalizeAttraction(raw, "order");
    const { adultPrice, childPrice, childEligible } = resolveAdultChildUnitPricesIls(
      r,
      line
    );
    const unit = resolveOptionalUnitPriceIls(r, line);
    const itemWithUnitPrice = {
      ...n,
      price:
        unit != null && Number.isFinite(unit)
          ? { value: unit, currency, type: "paid" }
          : n.price,
      state: {
        ...n.state,
        isAutoAttached: Boolean(
          r?.auto_attach === true || r?.auto_attach === "true"
        ),
      },
    };
    optionalAttractions.push(itemWithUnitPrice);

    const lineLocked = Boolean(
      line && dynamicPackageAttractionLineBillableToCustomer(line)
    );
    const idStr = String(itemWithUnitPrice.id);
    let adults = selectedQty[idStr]?.adults ?? 0;
    let children = selectedQty[idStr]?.children ?? 0;
    if (lineLocked) {
      if (adults === 0 && children === 0) {
        adults = 1;
      }
      children = 0;
    } else if (!childEligible) {
      children = 0;
    }

    const subtotalIls = optionalAttractionLineSubtotalIls({
      lineLocked,
      line,
      adultUnit: adultPrice,
      childUnit: childPrice,
      childEligible,
      adults,
      children,
    });

    optionalRows.push({
      item: itemWithUnitPrice,
      lineLocked,
      adultPrice,
      childPrice,
      childEligible,
      currency,
      selectedAdults: adults,
      selectedChildren: children,
      subtotalIls,
      rule: r,
    });
  }

  let feAddonUnpersistedIls = 0;
  for (const row of optionalRows) {
    const aid = row.item?.id != null ? Number(row.item.id) : NaN;
    if (!Number.isFinite(aid) || !optionalAidSet.has(aid)) continue;
    const line = lineByAttractionId.get(aid);
    if (line && dynamicPackageAttractionLineBillableToCustomer(line)) {
      continue;
    }
    if (row.selectedAdults + row.selectedChildren === 0) continue;
    feAddonUnpersistedIls += row.subtotalIls;
  }

  let optionalSelectedSumIls = 0;
  for (const row of optionalRows) {
    if (row.selectedAdults + row.selectedChildren === 0) continue;
    optionalSelectedSumIls += row.subtotalIls;
  }

  const displayAttractionsBillableIls =
    serverBillableTotalGross + feAddonUnpersistedIls;

  const selectedOptionalAttractions = optionalAttractions.filter((a) => {
    const idStr = String(a.id);
    const q = selectedQty[idStr];
    if (!q) return false;
    return q.adults > 0 || q.children > 0;
  });

  return {
    includedAttractions,
    giftAttractions,
    optionalAttractions,
    optionalRows,
    selectedOptionalQuantities: { ...selectedQty },
    selectedOptionalAttractions,
    pricing: {
      includedTotal: 0,
      serverBillableTotal: serverBillableTotalGross,
      feAddonUnpersistedIls,
      optionalSelectedTotal: optionalSelectedSumIls,
      totalAttractionsBillable: displayAttractionsBillableIls,
      displayAttractionsBillableIls,
      grandAttractionsTotal: displayAttractionsBillableIls,
      currency,
    },
  };
}
